import {
  getAirportSchema,
  getAnySchema,
  getCabinClassSchema,
  getCalendarSchema,
  getInfantsCountSchema,
  getNumberSchema,
  getProductTypeSchema,
  getRoomsSchema,
  getTripTypeSchema,
  getBooleanSchema
} from './schemas'

const PRODUCT_TYPE_ERROR = 'Invalid Product Type'
const AIRPORT_ERROR = 'Please enter an airport'
const ADULT_COUNT_ERROR = 'Invalid adultsCount'
const YOUTH_COUNT_ERROR = 'Invalid youthsCount'
const CHILDREN_COUNT_ERROR = 'Invalid childrenCount'
const INFANT_COUNT_ERROR = 'Invalid infantsCount'
const ROOMS_COUNT_ERROR = 'Invalid number of rooms'
const TYPEAHEAD_ERROR = 'Invalid Input'
const CALENDAR_ERROR = 'Enter a date'
const CABINCLASS_ERROR = 'Invalid Cabin Class'
const TRIPTYPE_ERROR = 'Invalid Trip Type'
const ALT_DATES_ERROR = 'Invalid flex date input' // TODO: HP_FLIGHT_FLEX_DATES cleanup
const ROOM_CAPACITY_ERROR = 'Rooms accommodate up to 4 people. You may need additional rooms for your group'
/**
 * Validation mappers: map a schema or schemas to set of form fields.
 *
 */

/**
 * flightValidationMapper takes a set of props and returns a validation schema for it.
 * @param props
 * @returns {{flights: {departureDate: (function(*)), departureAirport: *, arrivalDate: (function(*)), arrivalAirport: *}, tripType: *, cabinClass: *, productType: *}}
 */
const getFormattedResult = (key, result, customErrorMessage) => {
  if (result.error) {
    return {
      error: {
        message: customErrorMessage
      },
      field: key,
      isValid: false
    }
  }

  return {
    field: key,
    isValid: true
  }
}

const getError = result => {
  const { error, field } = result
  if (error) {
    return {
      [field]: error
    }
  }
  return null
}

export const getValidationErrors = (schemas, values) => {
  let errors = {}
  const keys = Object.keys(schemas)
  keys.forEach(key => {
    if (values[key]) {
      const newErrors = getError(schemas[key](values))
      errors = { ...errors, ...newErrors }
    }
  })
  return errors
}
/**
 *
 * @param props
 * @returns {{departureDate: (function(*)), departureAirport: *, arrivalDate: (function(*)), arrivalAirport: *, tripType: *, cabinClass: *, productType: *}}
 * @constructor
 */

export const validateSOPQInfantsCountSchema = item => {
  const { infantsCount } = item
  const isDepartNonUS =
    item.departureAirport && item.departureAirport.countryCode !== 'US'
  const isArrivalNonUS =
    item.arrivalAirport && item.arrivalAirport.countryCode !== 'US'
  const isInternational = isDepartNonUS || isArrivalNonUS
  const isError = infantsCount > 0 && isInternational
  const allowedCodes = isError ? 'US' : ''
  const nonUSCountryCode = isError ? 'Non-US' : ''

  const schema = getInfantsCountSchema(allowedCodes)
  const result = schema.validate({
    infantsCount: infantsCount,
    countryCode: nonUSCountryCode
  })
  return getFormattedResult('infantsCount', result, INFANT_COUNT_ERROR)
}

export const prepareCalendarSchema = (item, tripType, calendarOpen) => {
  const calendar = {
    departureDate: item.departureDate || null,
    arrivalDate: item.arrivalDate || null
  }

  const schemaConfig = {
    keys: [
      {
        name: 'departureDate',
        isRequired: true
      },
      {
        name: 'arrivalDate',
        isDisabled:
          tripType !== 'round-trip' ||
          typeof calendarOpen === 'number' ||
          calendarOpen === true
        // TODO refactor calendarOpen prop to be consistent type across all forms
      }
    ]
  }

  const schema = getCalendarSchema(schemaConfig)
  return schema.validate(calendar)
}

export const validateProductTypeSchema = values => {
  const { productType } = values
  const schema = getProductTypeSchema()
  const result = schema.validate({ productType })
  return getFormattedResult('productType', result, PRODUCT_TYPE_ERROR)
}

export const validateCalendarSchema = (values, key) => {
  const { tripType, calendarOpen, departureDate, arrivalDate } = values
  const result = prepareCalendarSchema(
    { departureDate, arrivalDate },
    tripType,
    calendarOpen
  )
  return getFormattedResult(key, result, CALENDAR_ERROR)
}

export const validateHotelCalendarSchema = (values, key) => {
  const { checkOutDate, checkInDate } = values
  const calendar = { checkOutDate, checkInDate }
  const schemaConfig = {
    keys: [
      {
        name: 'checkInDate',
        isRequired: false
      },
      {
        name: 'checkOutDate',
        isDisabled: false
      }
    ]
  }

  const schema = getCalendarSchema(schemaConfig)
  const result = schema.validate(calendar)
  return getFormattedResult(key, result, CALENDAR_ERROR)
}

export const validateTripTypeSchema = values => {
  const { tripType } = values
  const schema = getTripTypeSchema()
  const result = schema.validate({ tripType })
  return getFormattedResult('tripType', result, TRIPTYPE_ERROR)
}

export const validateRoomCapacity = values => {
  const { roomCount, infantsCount, childrenCount, adultsCount, youthsCount, productType } = values
  const totalNumPassengers = infantsCount + childrenCount + adultsCount + youthsCount
  const result = {}
  if (totalNumPassengers / 4 > roomCount) {
    result.error = true
  }
  if (productType && !productType.includes('STAY')) {
    result.error = false
  }
  return getFormattedResult('roomCount', result, ROOM_CAPACITY_ERROR)
}

export const validateCabinClass = values => {
  const { cabinClass } = values
  const schema = getCabinClassSchema()
  const result = schema.validate({ cabinClass })
  return getFormattedResult('cabinClass', result, CABINCLASS_ERROR)
}

export const validateDepartureAirport = (values, errors) => {
  const { infantsCount, departureAirport, otherAirport } = values
  const config = { key: 'departureAirport', infantsCount }
  let schema = getAirportSchema(config)
  const result = schema.validate({ departureAirport, otherAirport })
  const { error } = result
  // TODO: temporary work around until schema fix is worked out, then this can be removed
  if (
    departureAirport &&
    otherAirport &&
    departureAirport.itemName &&
    departureAirport.itemName === otherAirport.itemName
  ) {
    return getFormattedResult(
      'departureAirport',
      { error: true },
      "Departure and arrival airport can't be the same"
    )
  }

  const { key, errorMsg } = retrieveLapInfantsErrorForAirport(
    'departureAirport',
    values,
    error,
    errors
  )
  return getFormattedResult(key, result, errorMsg)
}

// Todo: Clean up this func, its redundant consider some updates....
export const validateArrivalAirport = (values, errors) => {
  const { infantsCount, arrivalAirport, otherAirport } = values
  const config = { key: 'arrivalAirport', infantsCount }
  const schema = getAirportSchema(config)
  const result = schema.validate({ arrivalAirport, otherAirport })
  const { error } = result

  // TODO: temporary work around until schema fix is worked out, then this can be removed
  if (
    arrivalAirport &&
    otherAirport &&
    arrivalAirport.itemName &&
    arrivalAirport.itemName === otherAirport.itemName
  ) {
    return getFormattedResult(
      'departureAirport',
      { error: true },
      "Arrival and departure airport can't be the same"
    )
  }

  const { key, errorMsg } = retrieveLapInfantsErrorForAirport(
    'arrivalAirport',
    values,
    error,
    errors
  )
  return getFormattedResult(key, result, errorMsg)
}

// Given a validated object, will return the correct parent key and error
export const retrieveLapInfantsErrorForAirport = (
  field,
  values,
  error,
  errors
) => {
  let key = field
  let errorMsg = null
  const { details } = error || {}
  const { infantsCount } = errors || {}

  if (!details && !infantsCount) {
    return { key }
  }

  if (
    values[field] &&
    values[field].countryCode === 'US' &&
    values.otherAirport &&
    values.otherAirport.countryCode === 'US' &&
    infantsCount
  ) {
    key = 'infantsCount'
    return { key }
  }

  if (
    details &&
    details[0] &&
    details[0].message === '"countryCode" must be one of [US]'
  ) {
    key = 'infantsCount'
    errorMsg = INFANT_COUNT_ERROR
  } else {
    errorMsg = AIRPORT_ERROR
  }

  return { key, errorMsg }
}

export const validateRoomsSchema = values => {
  const { rooms } = values
  const schema = getRoomsSchema()
  const result = schema.validate({ rooms })

  return getFormattedResult('rooms', result, ROOMS_COUNT_ERROR)
}

export const validateHotelsSchema = values => {
  return {
    calendar: validateHotelCalendarSchema(values, 'calendar'),
    rooms: validateRoomsSchema(values),
    typeahead: validateTypeaheadSchema(values)
  }
}

export const validateTypeaheadSchema = values => {
  const schema = getAnySchema('typeahead')
  const { typeahead } = values
  const result = schema.validate({ typeahead })

  return getFormattedResult('typeahead', result, TYPEAHEAD_ERROR)
}

export const validateAdultsCountSchema = values => {
  const { adultsCount } = values
  const isRequired = false
  const schema = getNumberSchema('adultsCount', isRequired)

  const result = schema.validate({ adultsCount })

  return getFormattedResult('adultsCount', result, ADULT_COUNT_ERROR)
}

export const validateYouthCountSchema = values => {
  const { youthsCount } = values
  const isRequired = false
  const schema = getNumberSchema('youthsCount', isRequired)

  const result = schema.validate({ youthsCount })

  return getFormattedResult('youthsCount', result, YOUTH_COUNT_ERROR)
}

export const validateChildrenCountSchema = values => {
  const { childrenCount } = values
  const isRequired = false
  const schema = getNumberSchema('childrenCount', isRequired)

  const result = schema.validate({ childrenCount })
  return getFormattedResult('childrenCount', result, CHILDREN_COUNT_ERROR)
}

// Would still love to get this simplfied more
export const validateInfantsCountSchema = values => {
  let allowedCodes = ''
  let nonUSCountryCode = ''
  const { infantsCount } = values
  if (infantsCount > 0 && values.flights) {
    values.flights.forEach(element => {
      if (
        values.flights &&
        ((element.departureAirport &&
          element.departureAirport.countryCode !== 'US') ||
          (element.arrivalAirport &&
            element.arrivalAirport.countryCode !== 'US'))
      ) {
        allowedCodes = 'US'
        nonUSCountryCode = 'Non-US'
      }
    })
  }

  const schema = getInfantsCountSchema(allowedCodes)
  const result = schema.validate({
    infantsCount: infantsCount,
    countryCode: nonUSCountryCode
  })
  return getFormattedResult('infantsCount', result, INFANT_COUNT_ERROR)
}

// TODO: HP_FLIGHT_FLEX_DATES seti cleanup
export const validateIsFlexDatesSchema = values => {
  const { isFlexDates } = values
  const isRequired = false
  const schema = getBooleanSchema('isFlexDates', isRequired)

  const result = schema.validate({ isFlexDates })

  return getFormattedResult('isFlexDates', result, ALT_DATES_ERROR)
}
